import { useState, useEffect } from 'react';
import { useProjectStore, useUIStore } from '@/stores';
import { Modal, Button } from '@/components/ui';
import type { VehicleNodes, MilestoneItem } from '@/types';

const defaultMilestones: MilestoneItem[] = [
  { id: '1', sequence: 1, name: 'VPI', description: 'Vehicle Program Initiation', required: false, functionalNode: 'N/A' },
  { id: '2', sequence: 2, name: 'SF', description: 'Styling Freeze', required: false, functionalNode: 'N/A' },
  { id: '3', sequence: 3, name: 'VDR', description: 'Verified Data Release', required: true, functionalNode: 'N/A' },
  { id: '4', sequence: 4, name: 'MC1', description: 'Matching 1', required: false, functionalNode: 'N/A' },
  { id: '5', sequence: 5, name: 'APPV', description: 'Advanced Process Validation Vehicle', required: true, functionalNode: '首次造车' },
  { id: '6', sequence: 6, name: 'MC2', description: 'Matching 2', required: false, functionalNode: 'N/A' },
  { id: '7', sequence: 7, name: 'PPV', description: 'Process Validation Vehicle', required: false, functionalNode: 'N/A' },
  { id: '8', sequence: 8, name: 'VTC', description: 'Vehicle Testing Complete', required: false, functionalNode: 'N/A' },
  { id: '9', sequence: 9, name: 'VDC', description: 'Validation Documentation Complete', required: false, functionalNode: 'N/A' },
  { id: '10', sequence: 10, name: 'S', description: 'Salable', required: true, functionalNode: '可销售车' },
  { id: '11', sequence: 11, name: 'SORP', description: 'Start Of Regular Production', required: true, functionalNode: 'N/A' },
];

interface NodeEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  nodes: Array<{
    key: string;
    label: string;
    date: string | undefined;
  }>;
}

export function NodeEditDialog({ isOpen, onClose, nodes }: NodeEditDialogProps) {
  const { currentProject, setCurrentProject } = useProjectStore();
  const { showToast } = useUIStore();
  const [nodeDates, setNodeDates] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const milestones = currentProject?.vehicleMilestoneTemplate?.milestones || defaultMilestones;
  
  const validMilestones = milestones.filter(m => m.name && m.name.trim() !== '');

  useEffect(() => {
    if (isOpen && nodes) {
      const dates: Record<string, string> = {};
      nodes.forEach(node => {
        dates[node.key] = node.date || '';
      });
      setNodeDates(dates);
    }
  }, [isOpen, nodes]);

  const handleNodeChange = (key: string, value: string) => {
    setNodeDates(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentProject) {
      showToast('没有当前项目', 'error');
      return;
    }

    const requiredMilestones = milestones.filter(m => m.required);
    for (const milestone of requiredMilestones) {
      if (!nodeDates[milestone.name]) {
        showToast(
          <span>请填写必填节点：<strong>{milestone.name} ({milestone.description})</strong></span>,
          'error'
        );
        return;
      }
    }

    setIsLoading(true);

    // 使用 updateVehicleNodes 而不是 setCurrentProject，这样不会触发 projectChanged 事件
    // Dashboard 会通过 hasNodesChanged 检测到变更
    useProjectStore.getState().updateVehicleNodes(nodeDates as VehicleNodes);

    showToast('节点时间更新成功', 'success');
    setIsLoading(false);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const getMilestoneConfig = (key: string) => {
    return milestones.find(m => m.name === key);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Vehicle Milestones"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-3 max-h-[400px] overflow-y-auto pr-2">
          {nodes.map((node) => {
            const config = getMilestoneConfig(node.key);
            return (
              <div key={node.key} className="flex flex-col">
                <label className="text-sm text-gray-600 mb-1">
                  {node.key} ({config?.description || node.label})
                  {config?.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type="date"
                  value={nodeDates[node.key] || ''}
                  onChange={(e) => handleNodeChange(node.key, e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            );
          })}
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
          >
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
